import React, { memo } from 'react';
import { Text } from '@cuemath/web-comps';
import { Separator } from '@cuemath/comps';
import COLORS from 'constants/colors';

import { KpiRow, KpiCard, UI } from '../../styled-comps';
import { METRICS, METRIC_LABELS, METRIC_COLORS, CONVERSIONS } from '../../constants';
import { conversionPct, deltaPct } from '../../helpers';

const COUNT_CARDS = [METRICS.QL, METRICS.TRIAL_DONE, METRICS.ENROLLED];

const formatNumber = value => (value === null || value === undefined ? '—' : value.toLocaleString());

// Shows the EXACT comparison value alongside the delta, e.g. "↑ +18% (vs 466)".
const DeltaText = ({ delta, compareValue, suffix, hasCompare }) => {
  if (!hasCompare) return null;
  if (delta === null || delta === undefined) {
    return (
      <Text variant="caption" color={UI.muted}>
        {`vs ${compareValue !== null && compareValue !== undefined ? compareValue : 'n/a'}`}
      </Text>
    );
  }
  const up = delta > 0;
  const flat = delta === 0;
  const color = flat ? UI.muted : up ? UI.up : UI.down;
  const arrow = flat ? '→' : up ? '↑' : '↓';
  return (
    <Text variant="caption" color={color}>
      {`${arrow} ${up ? '+' : ''}${delta}${suffix} (vs ${compareValue})`}
    </Text>
  );
};

// KPI strip: 3 volume metrics + 3 conversion ratios, each with delta vs comparison.
const KpiCards = memo(({ totals, compareTotals }) => {
  const hasCompare = !!compareTotals;

  const countCards = COUNT_CARDS.map(metric => {
    const value = totals[metric] || 0;
    const prev = hasCompare ? compareTotals[metric] || 0 : null;
    return {
      key: metric,
      accent: METRIC_COLORS[metric],
      label: METRIC_LABELS[metric],
      value: formatNumber(value),
      delta: deltaPct(value, prev),
      compareValue: hasCompare ? formatNumber(prev) : null,
      suffix: '%',
    };
  });

  const conversionCards = CONVERSIONS.map(conv => {
    const value = conversionPct(totals[conv.numerator], totals[conv.denominator]);
    const prev = hasCompare
      ? conversionPct(compareTotals[conv.numerator], compareTotals[conv.denominator])
      : null;
    const delta =
      value !== null && prev !== null ? Math.round((value - prev) * 10) / 10 : null;
    return {
      key: conv.key,
      accent: conv.color,
      label: conv.label,
      value: value === null ? '—' : `${value}%`,
      delta,
      compareValue: prev === null ? null : `${prev}%`,
      suffix: 'pp',
    };
  });

  const cards = [...countCards, ...conversionCards];

  return (
    <KpiRow>
      {cards.map(card => (
        <KpiCard key={card.key} $accent={card.accent}>
          <Text variant="eyebrow2" color={COLORS.BLACK_T_60}>
            {card.label}
          </Text>
          <Text variant="h4" color={UI.ink}>
            {card.value}
          </Text>
          <Separator height={2} transparent />
          <DeltaText
            delta={card.delta}
            compareValue={card.compareValue}
            suffix={card.suffix}
            hasCompare={hasCompare}
          />
        </KpiCard>
      ))}
    </KpiRow>
  );
});

export default KpiCards;
